import { useState, useEffect, useCallback } from 'react';
import { getPosts } from '../services/firebaseService';
import { Post } from '../types';
import { useAuth } from '../contexts/AuthContext';
import { useActivity } from './useActivity';

export interface UsePostsReturn {
  // États des posts
  posts: Post[];
  loading: boolean;
  loadingMore: boolean;
  error: string | null;
  hasMore: boolean;

  // Actions
  loadMorePosts: () => Promise<void>;
  refreshPosts: () => Promise<void>;
  createNewPost: (content: string) => Promise<void>;
  onPostCreated: (post: Post) => void;
  toggleLike: (postId: string, userId: string) => Promise<void>;
  addComment: (postId: string, content: string, userId: string) => Promise<void>;
}

export const usePosts = (filters?: any, userId?: string): UsePostsReturn => {
  const { user } = useAuth();
  const { recordLike, recordComment, recordPost } = useActivity();
  const [posts, setPosts] = useState<Post[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);

  const POSTS_PER_PAGE = 10;

  // Charger les posts initiaux
  useEffect(() => {
    loadInitialPosts();
  }, [userId]); // Ajouter userId dans les dépendances

  const loadInitialPosts = async () => {
    setLoading(true);
    setError(null);

    try {
      // Si un userId est fourni, filtrer les posts pour cet utilisateur
      const initialPosts = await getPosts(POSTS_PER_PAGE);
      const filteredPosts = userId
        ? initialPosts.filter(post => post.authorId === userId)
        : initialPosts;

      setPosts(filteredPosts);
      setCurrentPage(1);

      if (filteredPosts.length < POSTS_PER_PAGE) {
        setHasMore(false);
      }
    } catch (err) {
      setError('Erreur lors du chargement des posts');
      console.error('Erreur chargement posts:', err);
    } finally {
      setLoading(false);
    }
  };

  // Charger plus de posts (pagination)
  const loadMorePosts = useCallback(async () => {
    if (loading || loadingMore || !hasMore) return;

    setLoadingMore(true);
    setError(null);

    try {
      // Simuler une pagination en récupérant plus de posts
      const morePosts = await getPosts(POSTS_PER_PAGE * (currentPage + 1));

      // Prendre seulement les nouveaux posts
      const newPosts = morePosts.slice(posts.length);

      if (newPosts.length === 0) {
        setHasMore(false);
      } else {
        setPosts(prev => [...prev, ...newPosts]);
        setCurrentPage(prev => prev + 1);

        if (newPosts.length < POSTS_PER_PAGE) {
          setHasMore(false);
        }
      }
    } catch (err) {
      setError('Erreur lors du chargement de plus de posts');
      console.error('Erreur pagination posts:', err);
    } finally {
      setLoadingMore(false);
    }
  }, [loading, loadingMore, hasMore, posts.length, currentPage]);

  // Rafraîchir les posts
  const refreshPosts = useCallback(async () => {
    setCurrentPage(1);
    setHasMore(true);
    await loadInitialPosts();
  }, []);

  // Créer un nouveau post
  const createNewPost = useCallback(async (content: string) => {
    if (!user?.id) {
      setError('Vous devez être connecté pour créer un post');
      return;
    }

    try {
      // Créer le nouveau post avec un ID temporaire
      const newPost: Post = {
        id: Date.now().toString(),
        content,
        authorId: user.id,
        mediaUrls: [],
        links: [],
        category: 'general',
        likes: [],
        comments: [],
        shares: 0,
        views: 0,
        isDeleted: false,
        isPinned: false,
        tags: [],
        // Thread properties
        isThread: false,
        createdAt: new Date()
      };

      // Ajouter le post en haut de la liste
      setPosts(prev => [newPost, ...prev]);

      // Enregistrer l'activité de création de post
      if (user.username || user.displayName) {
        await recordPost(
          user.id, 
          newPost.id, 
          user.username || user.id, 
          user.displayName || user.username || `User ${user.id.substring(0, 8)}`
        );
      }
    } catch (err) {
      setError('Erreur lors de la création du post');
      console.error('Erreur création post:', err);
    }
  }, [user, recordPost]);

  // Toggle like sur un post
  const toggleLike = useCallback(async (postId: string, userId: string) => {
    if (!user) return;
    
    try {
      setPosts(prev => prev.map(post => {
        if (post.id === postId) {
          const isLiked = post.likes.includes(userId);
          const newLikes = isLiked
            ? post.likes.filter(id => id !== userId)
            : [...post.likes, userId];

          // Enregistrer l'activité de like seulement si ce n'était pas déjà liké
          if (!isLiked && (user.username || user.displayName)) {
            recordLike(
              userId, 
              postId, 
              user.username || user.id, 
              user.displayName || user.username || `User ${user.id.substring(0, 8)}`
            );
          }

          return {
            ...post,
            likes: newLikes
          };
        }
        return post;
      }));
    } catch (err) {
      setError('Erreur lors du like');
      console.error('Erreur toggle like:', err);
    }
  }, [user, recordLike]);

  // Ajouter un commentaire
  const addComment = useCallback(async (postId: string, content: string, userId: string) => {
    if (!user) return;

    try {
      const newComment = {
        id: Date.now().toString(),
        postId,
        authorId: userId,
        content,
        likes: [],
        isDeleted: false,
        createdAt: new Date()
      };

      setPosts(prev => prev.map(post => {
        if (post.id === postId) {
          const newComments = [...post.comments, newComment];
          return {
            ...post,
            comments: newComments
          };
        }
        return post;
      }));

      // Enregistrer l'activité de commentaire
      if (user.username || user.displayName) {
        await recordComment(
          userId, 
          postId, 
          content, 
          user.username || user.id, 
          user.displayName || user.username || `User ${user.id.substring(0, 8)}`
        );
      }
    } catch (err) {
      setError('Erreur lors de l\'ajout du commentaire');
      console.error('Erreur ajout commentaire:', err);
    }
  }, [user, recordComment]);

  // Fonction pour ajouter un post créé (compatible avec CreatePost)
  const onPostCreated = useCallback((post: Post) => {
    setPosts(prev => [post, ...prev]);
  }, []);

  return {
    posts,
    loading,
    loadingMore,
    error,
    hasMore,
    loadMorePosts,
    refreshPosts,
    createNewPost,
    onPostCreated,
    toggleLike,
    addComment
  };
};
